import React from "react"
import { useTranslation } from "react-i18next"

import type { UserRoleType } from "@/models/user-role"
import { nonAdminUserRoles } from "@/models/user-role"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"

import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Typography from "@mui/material/Typography"

interface RoleSelectionDialogProps {
  confirmText?: string
  description?: string
  onClose: () => void
  onConfirm: (role: UserRoleType) => void
  open: boolean
  title: string
}

const itemHeight = 60

export const RoleSelectionDialog: React.FC<RoleSelectionDialogProps> = ({
  confirmText = "OK",
  description,
  onClose,
  onConfirm,
  open,
  title,
}) => {
  const { t } = useTranslation()
  const [selectedRole, setSelectedRole] = React.useState<
    UserRoleType | undefined
  >()

  const reset = React.useCallback((): void => {
    setSelectedRole(undefined)
  }, [setSelectedRole])

  const handleClose = React.useCallback((): void => {
    onClose()
    reset()
  }, [onClose, reset])

  const handleConfirm = React.useCallback((): void => {
    if (selectedRole) {
      onConfirm(selectedRole)
      reset()
    }
  }, [onConfirm, reset, selectedRole])

  const handleListItemClick = (role: UserRoleType): void => {
    setSelectedRole(role)
  }

  return (
    <Dialog
      aria-describedby="role-selection-dialog-description"
      aria-labelledby="role-selection-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="role-selection-dialog-title" onClose={handleClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        {description && (
          <Typography
            id="role-selection-dialog-description"
            sx={{ mb: 2, overflowY: "auto" }}
          >
            {description}
          </Typography>
        )}
        <List
          aria-label="roles"
          sx={{
            height: `calc(${itemHeight}px * 4.5 + 8px)`,
            overflowY: "auto",
          }}
        >
          {nonAdminUserRoles.map((role) => (
            <ListItemButton
              key={role.id}
              onClick={() => {
                handleListItemClick(role.id)
              }}
              selected={role.id === selectedRole}
            >
              <ListItemText
                primary={t(role.name, { ns: "common" })}
                secondary={t(role.description, { ns: "common" })}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t("button.cancel", { ns: "common" })}
        </Button>
        <Button
          disabled={!selectedRole}
          onClick={handleConfirm}
          variant="contained"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
